(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.WallForgeStudioState = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  function empty(designId) {
    return { contract: 1, designId: String(designId), photos: [], versions: [], selectedId: null,
      attempt: null, screen: 'upload', variantId: null, physical: {}, cartAttempt: null, cartOperations: [] };
  }

  function selected(state) {
    return completedVersions(state).find(version => version.id === state.selectedId) || null;
  }

  // History and in-flight work survive visits; a plain product URL starts browsing.
  function enterProduct(state) {
    state.screen = 'upload'; state.flowScreen = null; state.selectedId = null;
    state.photos = []; state.replacementFor = null;
    state.failedAttempt = null; state.generationIssue = null;
    return state;
  }

  function configurationChoice(state, current, choice) {
    state.physical ||= {};
    if (current && !current.isDigital) {
      state.physical[current.type] = { frame: current.frame, size: current.size };
      state.physicalFrame = current.frame;
    }
    if (choice.type !== 'digital' && choice.frame === undefined && choice.size === undefined) {
      return { ...state.physical[choice.type], ...(state.physicalFrame ? { frame: state.physicalFrame } : {}), ...choice };
    }
    return choice;
  }

  // Only completed, bound artwork from this design belongs in customer history.
  // Older stored completions have no status field; attempts live separately.
  function completedVersions(state) {
    const seen = new Set();
    return (state.versions || []).filter(version => {
      if (!version || version.unavailable || String(version.designId) !== String(state.designId)
        || !version.id || !version.jobId || !version.binding || !version.previewUrl
        || (version.status && version.status !== 'completed') || seen.has(version.id)) return false;
      seen.add(version.id);
      return true;
    });
  }

  function selectVersion(state, id) {
    if (!completedVersions(state).some(version => version.id === id)) return false;
    state.selectedId = id;
    restoreSources(state);
    return true;
  }

  function libraryGroups(records, preferredDesignId) {
    const groups = [];
    for (const { key, state } of records) {
      if (state?.contract !== 1 || !state.designId || !Array.isArray(state.versions) || typeof state.design?.title !== 'string'
        || !/^[a-z0-9][a-z0-9-]*$/.test(state.design.handle || '')) continue;
      // Number the original completed records, including unavailable versions,
      // so expiring one version never renumbers its surviving siblings.
      const numbered = completedVersions({ ...state, versions: (state.versions || []).map(v => v && ({ ...v, unavailable: false })) });
      const versions = completedVersions(state).map(version => ({ version,
        number: numbered.findIndex(item => item.id === version.id) + 1 }));
      versions.sort((a, b) => (Number(b.version.completedAt) || b.number) - (Number(a.version.completedAt) || a.number) || b.number - a.number);
      if (versions.length) groups.push({ key, designId: String(state.designId), design: state.design, versions });
    }
    return groups.sort((a, b) => {
      if (a.designId === String(preferredDesignId)) return -1;
      if (b.designId === String(preferredDesignId)) return 1;
      return (Number(b.versions[0].version.completedAt) || 0) - (Number(a.versions[0].version.completedAt) || 0) || a.design.title.localeCompare(b.design.title);
    });
  }

  function versionNumber(state, id) {
    return completedVersions({ ...state, versions: (state.versions || []).map(version => version && ({ ...version, unavailable: false })) }).findIndex(version => version.id === id) + 1;
  }

  // File/Blob bytes are immutable; copy each reference record and its containing list.
  const copyReferences = references => (references || []).map(reference => Object.assign({}, reference));

  // Never infer an older version's sources from the mutable upload controls.
  function photosFor(version) {
    const photos = version?.photos;
    return Array.isArray(photos) && photos.length && photos.every(photo => photo.file instanceof Blob && photo.file.size > 0)
      ? copyReferences(photos) : [];
  }

  function restoreSources(state) {
    state.photos = photosFor(selected(state));
    state.replacementFor = null;
  }

  function sourceAssets(attempt) {
    if (attempt.kind === 'edit') return copyReferences(attempt.sourceAssets);
    const uploads = attempt.batch ? attempt.init?.uploads : [attempt.init?.upload];
    return (uploads || []).filter(Boolean).map((upload, index) => ({
      jobId: attempt.jobId, assetKey: upload.assetKey || upload.uploadKey,
      uploadId: upload.uploadId || null, position: index + 1
    })).filter(source => source.assetKey);
  }

  function complete(state, attemptId, version) {
    if (!state.attempt || state.attempt.id !== attemptId || String(version.designId) !== state.designId) return false;
    if (state.attempt.jobId && state.attempt.jobId !== version.jobId) return false;
    if (state.attempt.designId && state.attempt.designId !== state.designId) return false;
    if (!version.id || !version.previewUrl || !version.binding) return false;
    if (!state.versions.some(item => item.id === version.id)) state.versions.push(Object.assign({}, version, {
      photos: copyReferences(state.attempt.photos), sourceAssets: sourceAssets(state.attempt),
      sourceVersionId: state.attempt.sourceId || null
    }));
    state.selectedId = version.id;
    restoreSources(state);
    state.attempt = null;
    state.screen = 'result';
    return true;
  }

  function dimensions(label) {
    const match = String(label).replace(/,/g, '.').match(/(\d+(?:\.\d+)?)\s*(?:["″]|in|cm)?\s*[x×]\s*(\d+(?:\.\d+)?)/i);
    return match ? [Number(match[1]), Number(match[2])] : null;
  }

  function catalog(product, semantics) {
    const optionNames = (product.options || []).map(option => typeof option === 'string' ? option : option.name);
    return product.variants.map(variant => {
      const semantic = semantics.buildSemanticVariant(variant, null, optionNames);
      const type = semantic.baseType === 'print' ? 'poster' : semantic.baseType;
      const size = dimensions(semantic.size);
      return Object.assign({}, variant, semantic, { id: String(variant.id), type,
        area: size ? size[0] * size[1] : Infinity,
        fitsPortrait: semantic.isDigital || Boolean(size && Math.abs(size[0] / size[1] - 2 / 3) < 0.005),
        supportedOption: semantic.isDigital || Boolean(size && ['12x18', '20x30', '24x36'].includes(size.join('x')) && ['none', 'black', 'white'].includes(semantic.frame)) });
    }).filter(variant => ['poster', 'canvas', 'digital'].includes(variant.type));
  }

  function allowed(variants, version) {
    const ids = version && version.allowedVariantIds;
    return variants.filter(variant => variant.fitsPortrait && variant.supportedOption !== false
      && (variant.isDigital || !version?.aspectRatio || version.aspectRatio === '2:3')
      && (!Array.isArray(ids) || (!ids.length && !version?.explicitVariantRestrictions) || ids.map(String).includes(variant.id)));
  }

  function choose(variants, desired) {
    const available = variants.filter(variant => variant.available);
    const exact = available.find(variant => variant.id === String(desired && desired.id));
    if (exact) return exact;
    const choice = desired || {};
    const type = choice.type || (available.some(variant => variant.type === 'poster') ? 'poster' : available[0]?.type);
    const typed = available.filter(variant => variant.type === type);
    const framed = typed.filter(variant => variant.frame === (choice.frame || 'none'));
    const candidates = framed;
    if (choice.size) return candidates.find(variant => variant.size === choice.size) || null;
    return candidates.slice().sort((a, b) => a.area - b.area)[0] || null;
  }

  function initializeConfiguration(state, variants) {
    if (state.configuration) return;
    const firstPrint = choose(variants, { type: 'poster' }) || variants.filter(item => item.available && item.type === 'poster').sort((a, b) => a.area - b.area)[0];
    const variant = firstPrint || choose(variants);
    state.variantId = variant?.id || null;
    if (variant) state.configuration = { id: variant.id, type: variant.type, frame: variant.frame, size: variant.size };
  }

  function cartProperties(version, operationId, variant) {
    if (!version?.binding || !version?.id || !version?.jobId) throw new Error('ARTWORK_BINDING_MISSING');
    const properties = { '_Custom Design': version.cartPreviewUrl || version.previewUrl,
      '_Print File': version.printRef || version.jobId, '_wallforge_job_id': version.jobId,
      '_wallforge_artwork_binding': version.binding, '_wallforge_artwork_revision': version.id,
      '_wallforge_cart_operation': operationId,
      '_wallforge_digital_entitlement': variant.isDigital ? 'standalone' : 'included_with_physical' };
    if (version.personalization?.schemaVersion && version.personalization?.personalizationHash) {
      properties._wallforge_personalization_version = String(version.personalization.schemaVersion);
      properties._wallforge_personalization_hash = version.personalization.personalizationHash;
    }
    return properties;
  }

  function findCartItem(cart, operationId) {
    return (cart.items || []).find(item => item.properties?._wallforge_cart_operation === operationId) || null;
  }

  return { empty, selected, enterProduct, configurationChoice, completedVersions, selectVersion, libraryGroups, versionNumber, photosFor, restoreSources, sourceAssets, complete, dimensions, catalog, allowed, choose, initializeConfiguration, cartProperties, findCartItem };
});
